import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { characterAPI } from '../services/api';
import CustomSelect from '../components/CustomSelect';
import './CharacterCreate.css';

const STEPS = [
  'Basic Info',
  'Gender',
  'Species',
  'Class',
  'Background',
  'Appearance',
  'Abilities',
  'Languages',
  'Starting Gear',
  'Spells',
  'Review'
];

const ABILITY_NAMES = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'];

// Species icons/emojis for visual distinction
const SPECIES_ICONS = {
  human: '👤',
  elf: '🧝',
  dwarf: '⛏️',
  halfling: '🍀',
  gnome: '🔧',
  'half-elf': '🧝‍♂️',
  'half-orc': '👹',
  tiefling: '😈',
  dragonborn: '🐲',
  aasimar: '😇',
  goliath: '🏔️',
  tabaxi: '🐱',
  kenku: '🐦',
  lizardfolk: '🦎',
  firbolg: '🌲',
  tortle: '🐢'
};

// Class icons
const CLASS_ICONS = {
  barbarian: '⚔️',
  bard: '🎵',
  cleric: '✝️',
  druid: '🌿',
  fighter: '🛡️',
  monk: '👊',
  paladin: '⚜️',
  ranger: '🏹',
  rogue: '🗡️',
  sorcerer: '✨',
  warlock: '🔮',
  wizard: '📖'
};

export default function CharacterCreate() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [creationData, setCreationData] = useState(null);

  const [character, setCharacter] = useState({
    name: '',
    gender: '',
    species: { primary: '', secondary: null },
    class: '',
    background: { name: '', story: '', guild: null },
    appearance: { height: 170, weight: 70, description: '', age: 25, portraitUrl: '' },
    abilityScores: { strength: 10, dexterity: 10, constitution: 10, intelligence: 10, wisdom: 10, charisma: 10 },
    languages: ['common'],
    selectedWeapon: '',
    selectedClothing: '',
    selectedCantrips: [],
    selectedSpells: []
  });

  const [pointsRemaining, setPointsRemaining] = useState(27);
  const [generatingBackstory, setGeneratingBackstory] = useState(false);
  const [generatingAppearance, setGeneratingAppearance] = useState(false);
  const [generatingPortrait, setGeneratingPortrait] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);

  useEffect(() => {
    loadCreationData();
  }, []);

  useEffect(() => {
    // Calculate points remaining for point buy
    const costs = { 8: 0, 9: 1, 10: 2, 11: 3, 12: 4, 13: 5, 14: 7, 15: 9 };
    const spent = ABILITY_NAMES.reduce((total, ability) => {
      const score = character.abilityScores[ability];
      return total + (costs[score] || 0);
    }, 0);
    setPointsRemaining(27 - spent);
  }, [character.abilityScores]);

  const loadCreationData = async () => {
    try {
      const response = await characterAPI.getCreationData();
      setCreationData(response.data);
    } catch (err) {
      setError('Failed to load character creation data');
    } finally {
      setLoading(false);
    }
  };

  const updateCharacter = (field, value) => {
    setCharacter(prev => ({ ...prev, [field]: value }));
  };

  const updateNested = (parent, field, value) => {
    setCharacter(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const handleAbilityChange = (ability, delta) => {
    const current = character.abilityScores[ability];
    const newValue = current + delta;
    if (newValue < 8 || newValue > 15) return;

    const costs = { 8: 0, 9: 1, 10: 2, 11: 3, 12: 4, 13: 5, 14: 7, 15: 9 };
    const currentCost = costs[current];
    const newCost = costs[newValue];
    const pointChange = newCost - currentCost;

    if (pointsRemaining - pointChange < 0) return;

    setCharacter(prev => ({
      ...prev,
      abilityScores: { ...prev.abilityScores, [ability]: newValue }
    }));
  };

  const toggleLanguage = (lang) => {
    setCharacter(prev => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang]
    }));
  };

  const toggleCantrip = (cantrip) => {
    const classData = creationData?.classes.find(c => c.id === character.class);
    const maxCantrips = classData?.cantripsKnown?.[1] || 0;

    setCharacter(prev => {
      if (prev.selectedCantrips.includes(cantrip)) {
        return { ...prev, selectedCantrips: prev.selectedCantrips.filter(c => c !== cantrip) };
      }
      if (prev.selectedCantrips.length >= maxCantrips) return prev;
      return { ...prev, selectedCantrips: [...prev.selectedCantrips, cantrip] };
    });
  };

  const toggleSpell = (spell) => {
    const classData = creationData?.classes.find(c => c.id === character.class);
    const maxSpells = classData?.spellsKnown?.[1] || 2;

    setCharacter(prev => {
      if (prev.selectedSpells.includes(spell)) {
        return { ...prev, selectedSpells: prev.selectedSpells.filter(s => s !== spell) };
      }
      if (prev.selectedSpells.length >= maxSpells) return prev;
      return { ...prev, selectedSpells: [...prev.selectedSpells, spell] };
    });
  };

  const handleGenerateBackstory = async () => {
    if (!character.name || !character.species.primary || !character.class || !character.background.name) {
      setError('Please fill in name, species, class, and background before generating a backstory.');
      return;
    }

    setGeneratingBackstory(true);
    setError('');

    try {
      const response = await characterAPI.generateBackstory({
        name: character.name,
        species: character.species.primary,
        characterClass: character.class,
        background: character.background.name,
        abilities: character.abilityScores
      });
      updateNested('background', 'story', response.data.backstory);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate backstory. Please try again.');
    } finally {
      setGeneratingBackstory(false);
    }
  };

  const handleGenerateAppearance = async () => {
    if (!character.name || !character.species.primary || !character.class) {
      setError('Please fill in name, species, and class before generating an appearance.');
      return;
    }

    // Clear existing description and portrait before generating new one
    updateNested('appearance', 'description', '');
    updateNested('appearance', 'portraitUrl', '');

    setGeneratingAppearance(true);
    setError('');

    try {
      const response = await characterAPI.generateAppearance({
        name: character.name,
        species: character.species.primary,
        characterClass: character.class,
        background: character.background.name,
        height: character.appearance.height,
        weight: character.appearance.weight,
        age: character.appearance.age
      });
      updateNested('appearance', 'description', response.data.description);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate appearance. Please try again.');
    } finally {
      setGeneratingAppearance(false);
    }
  };

  const handleGeneratePortrait = async (forceRegenerate = false) => {
    // If portrait exists and not forcing, show confirmation
    if (character.appearance.portraitUrl && !forceRegenerate) {
      setShowRegenerateConfirm(true);
      return;
    }

    if (!character.name || !character.species.primary || !character.class) {
      setError('Please fill in name, species, and class before generating a portrait.');
      return;
    }

    setGeneratingPortrait(true);
    setShowRegenerateConfirm(false);
    setError('');

    try {
      const response = await characterAPI.generatePortrait({
        name: character.name,
        species: character.species.primary,
        secondarySpecies: character.species.secondary,
        gender: character.gender,
        characterClass: character.class,
        appearance: character.appearance.description
      });
      updateNested('appearance', 'portraitUrl', response.data.portraitUrl);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate portrait. Please try again.');
    } finally {
      setGeneratingPortrait(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    try {
      await characterAPI.create(character);
      navigate('/play');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create character');
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 0: return character.name.trim().length >= 1;
      case 1: return character.gender !== '';
      case 2: return character.species.primary !== '';
      case 3: return character.class !== '';
      case 4: {
        if (character.background.name === '') return false;
        const bg = creationData?.backgrounds.find(b => b.id === character.background.name);
        if (bg?.requiresChoice === 'guild' && !character.background.guild) return false;
        return true;
      }
      case 6: return pointsRemaining >= 0;
      default: return true;
    }
  };

  const getSelectedClassData = () => {
    return creationData?.classes.find(c => c.id === character.class);
  };

  const isSpellcaster = () => {
    const classData = getSelectedClassData();
    return classData?.spellcaster === true;
  };

  const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1).replace(/-/g, ' ') : '';

  if (loading) {
    return (
      <div className="create-loading">
        <div className="spinner"></div>
        <p>Preparing the forge...</p>
      </div>
    );
  }

  const renderStep = () => {
    switch (step) {
      case 0: // Basic Info
        return (
          <div className="step-content">
            <h2>Name Your Character</h2>
            <p className="step-description">Choose a name that fits your adventurer</p>
            <div className="form-group">
              <label>Character Name</label>
              <input
                type="text"
                value={character.name}
                onChange={(e) => updateCharacter('name', e.target.value)}
                placeholder="Enter character name"
                maxLength={50}
                autoFocus
              />
            </div>
          </div>
        );

      case 1: // Gender
        return (
          <div className="step-content">
            <h2>Choose Your Gender</h2>
            <p className="step-description">Select your character's gender identity</p>
            <div className="selection-grid gender-grid">
              {[
                { id: 'male', label: 'Male', icon: '♂️' },
                { id: 'female', label: 'Female', icon: '♀️' },
                { id: 'trans-male', label: 'Trans Male', icon: '⚧️' },
                { id: 'trans-female', label: 'Trans Female', icon: '⚧️' }
              ].map(gender => (
                <div
                  key={gender.id}
                  className={`selection-card gender-card ${character.gender === gender.id ? 'selected' : ''}`}
                  onClick={() => updateCharacter('gender', gender.id)}
                >
                  <div className="gender-icon">{gender.icon}</div>
                  <h4>{gender.label}</h4>
                </div>
              ))}
            </div>
          </div>
        );

      case 2: // Species
        return (
          <div className="step-content">
            <h2>Choose Your Species</h2>
            <p className="step-description">Your species determines certain traits and abilities</p>
            <div className="selection-grid species-grid">
              {creationData?.species.map(species => (
                <div
                  key={species.id}
                  className={`selection-card species-card ${character.species.primary === species.id ? 'selected' : ''}`}
                  onClick={() => updateNested('species', 'primary', species.id)}
                >
                  <div className="species-icon">{SPECIES_ICONS[species.id] || '👤'}</div>
                  <div className="species-info">
                    <h4>{species.name}</h4>
                    <p className="selection-description">{species.description}</p>
                    <p className="selection-traits">
                      <strong>Traits:</strong> {species.traits?.slice(0, 3).join(', ')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="form-group mt-3">
              <CustomSelect
                label="Secondary Species (Optional)"
                placeholder="None - Single heritage"
                value={character.species.secondary || ''}
                onChange={(val) => updateNested('species', 'secondary', val || null)}
                options={[
                  { id: '', name: 'None - Single heritage' },
                  ...creationData?.species.filter(s => s.id !== character.species.primary) || []
                ]}
                renderOption={(opt) => (
                  <div className="select-option-with-image">
                    <span className="select-option-image placeholder-img">
                      {SPECIES_ICONS[opt.id] || '—'}
                    </span>
                    <span className="select-option-name">{opt.name}</span>
                  </div>
                )}
                renderSelected={(opt) => (
                  <div className="select-option-with-image">
                    <span>{SPECIES_ICONS[opt.id] || '—'}</span>
                    <span>{opt.name}</span>
                  </div>
                )}
              />
            </div>
          </div>
        );

      case 3: // Class
        return (
          <div className="step-content">
            <h2>Choose Your Class</h2>
            <p className="step-description">Your class determines your abilities and role in combat</p>
            <div className="selection-grid class-grid">
              {creationData?.classes.map(cls => (
                <div
                  key={cls.id}
                  className={`selection-card class-card ${character.class === cls.id ? 'selected' : ''}`}
                  onClick={() => updateCharacter('class', cls.id)}
                >
                  <div className="class-icon">{CLASS_ICONS[cls.id] || '⚔️'}</div>
                  <div className="class-info">
                    <h4>{cls.name}</h4>
                    <p className="selection-description">{cls.description}</p>
                    <p className="selection-traits">
                      <strong>Hit Die:</strong> d{cls.hitDie} | <strong>Primary:</strong> {capitalize(cls.primaryAbility)}
                      {cls.spellcaster && <span className="spellcaster-badge">Spellcaster</span>}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 4: // Background
        const selectedBackground = creationData?.backgrounds.find(bg => bg.id === character.background.name);
        const requiresGuild = selectedBackground?.requiresChoice === 'guild';

        return (
          <div className="step-content">
            <h2>Choose Your Background</h2>
            <p className="step-description">Your background shapes who you were before becoming an adventurer</p>
            <div className="selection-grid">
              {creationData?.backgrounds.map(bg => (
                <div
                  key={bg.id}
                  className={`selection-card ${character.background.name === bg.id ? 'selected' : ''}`}
                  onClick={() => {
                    setCharacter(prev => ({
                      ...prev,
                      background: { ...prev.background, name: bg.id, guild: null }
                    }));
                  }}
                >
                  <h4>{bg.name}</h4>
                  <p className="selection-description">{bg.description}</p>
                  <p className="selection-traits">
                    <strong>Skills:</strong> {bg.skills?.join(', ')} | <strong>Gold:</strong> {bg.gold}
                  </p>
                </div>
              ))}
            </div>

            {requiresGuild && (
              <div className="form-group mt-3 guild-selection">
                <CustomSelect
                  label="Select Your Guild"
                  required
                  placeholder="-- Select a guild --"
                  value={character.background.guild?.id || ''}
                  onChange={(val) => {
                    const guild = selectedBackground.choices.find(g => g.id === val);
                    setCharacter(prev => ({
                      ...prev,
                      background: {
                        ...prev.background,
                        guild: guild ? { id: guild.id, name: guild.name, tools: guild.tools } : null
                      }
                    }));
                  }}
                  options={selectedBackground?.choices || []}
                  renderOption={(guild) => (
                    <div className="guild-option">
                      <span className="guild-option-name">{guild.name}</span>
                      <span className="guild-option-tools">{guild.tools}</span>
                    </div>
                  )}
                  renderSelected={(guild) => (
                    <span>{guild.name}</span>
                  )}
                />
                {character.background.guild && (
                  <p className="guild-info mt-1">
                    You will gain proficiency with <strong>{character.background.guild.tools}</strong>
                  </p>
                )}
              </div>
            )}

            <div className="form-group mt-3">
              <div className="backstory-label-row">
                <label>Background Story (Optional)</label>
                <button
                  type="button"
                  className="btn btn-magic"
                  onClick={handleGenerateBackstory}
                  disabled={generatingBackstory || !character.name || !character.species.primary || !character.class || !character.background.name}
                  title="Generate backstory with AI"
                >
                  {generatingBackstory ? (
                    <span className="magic-spinner"></span>
                  ) : (
                    <span className="magic-wand">✨</span>
                  )}
                  {generatingBackstory ? 'Generating...' : 'AI Generate'}
                </button>
              </div>
              <textarea
                value={character.background.story}
                onChange={(e) => updateNested('background', 'story', e.target.value)}
                placeholder="Tell us about your character's past... or click the AI button to generate one!"
                rows={6}
                disabled={generatingBackstory}
              />
            </div>
          </div>
        );

      case 5: // Appearance
        return (
          <div className="step-content">
            <h2>Describe Your Appearance</h2>
            <p className="step-description">How does your character look?</p>

            <div className="appearance-controls">
              <div className="slider-group">
                <label>Height: {character.appearance.height} cm ({Math.floor(character.appearance.height / 30.48)}'{Math.round((character.appearance.height % 30.48) / 2.54)}")</label>
                <input
                  type="range"
                  min="100"
                  max="250"
                  value={character.appearance.height}
                  onChange={(e) => updateNested('appearance', 'height', parseInt(e.target.value))}
                />
              </div>

              <div className="slider-group">
                <label>Weight: {character.appearance.weight} kg ({Math.round(character.appearance.weight * 2.205)} lbs)</label>
                <input
                  type="range"
                  min="30"
                  max="200"
                  value={character.appearance.weight}
                  onChange={(e) => updateNested('appearance', 'weight', parseInt(e.target.value))}
                />
              </div>

              <div className="slider-group">
                <label>Age: {character.appearance.age} years</label>
                <input
                  type="range"
                  min="16"
                  max="500"
                  value={character.appearance.age}
                  onChange={(e) => updateNested('appearance', 'age', parseInt(e.target.value))}
                />
              </div>
            </div>

            <div className="appearance-layout">
              <div className="appearance-description-section">
                <div className="form-group">
                  <div className="backstory-label-row">
                    <label>Physical Description</label>
                    <button
                      type="button"
                      className="btn btn-magic"
                      onClick={handleGenerateAppearance}
                      disabled={generatingAppearance || !character.name || !character.species.primary || !character.class}
                      title="Generate appearance with AI"
                    >
                      {generatingAppearance ? (
                        <span className="magic-spinner"></span>
                      ) : (
                        <span className="magic-wand">✨</span>
                      )}
                      {generatingAppearance ? 'Generating...' : (character.appearance.description ? 'Generate New' : 'AI Generate')}
                    </button>
                  </div>
                  <textarea
                    value={character.appearance.description}
                    onChange={(e) => updateNested('appearance', 'description', e.target.value)}
                    placeholder="Describe your character's appearance - hair color, eye color, distinguishing features, clothing style... or click the AI button to generate one!"
                    rows={8}
                    disabled={generatingAppearance}
                  />
                </div>
              </div>

              <div className="appearance-portrait-section">
                <label>Character Portrait</label>
                <div className="portrait-container">
                  {generatingPortrait ? (
                    <div className="portrait-loading">
                      <div className="portrait-spinner"></div>
                      <p>Generating portrait...</p>
                    </div>
                  ) : character.appearance.portraitUrl ? (
                    <>
                      <img
                        src={character.appearance.portraitUrl}
                        alt={`Portrait of ${character.name}`}
                        className="character-portrait"
                      />
                      <button
                        type="button"
                        className="portrait-regenerate-btn"
                        onClick={() => handleGeneratePortrait(false)}
                        title="Regenerate portrait"
                      >
                        <span>&#x21bb;</span>
                      </button>
                    </>
                  ) : (
                    <div className="portrait-placeholder">
                      <button
                        type="button"
                        className="btn btn-magic btn-generate-portrait"
                        onClick={() => handleGeneratePortrait(true)}
                        disabled={generatingPortrait || !character.name || !character.species.primary || !character.class || !character.appearance.description}
                      >
                        <span className="magic-wand">✨</span>
                        Generate Portrait
                      </button>
                      <p className="portrait-hint">
                        {character.appearance.description ? 'AI-generated character art' : 'Add a description first'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Regenerate Confirmation Modal */}
            {showRegenerateConfirm && (
              <div className="modal-overlay">
                <div className="modal-content regenerate-modal">
                  <h3>Regenerate Portrait?</h3>
                  <p>This will replace your current portrait with a new one. Are you sure?</p>
                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowRegenerateConfirm(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => handleGeneratePortrait(true)}
                    >
                      Regenerate
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 6: // Abilities
        return (
          <div className="step-content">
            <h2>Set Ability Scores</h2>
            <p className="step-description">
              Distribute your points (Point Buy system). Points remaining: <strong className={pointsRemaining < 0 ? 'text-danger' : 'text-gold'}>{pointsRemaining}</strong>
            </p>
            <div className="ability-grid">
              {ABILITY_NAMES.map(ability => (
                <div key={ability} className="ability-control">
                  <label>{capitalize(ability)}</label>
                  <div className="ability-adjuster">
                    <button
                      onClick={() => handleAbilityChange(ability, -1)}
                      disabled={character.abilityScores[ability] <= 8}
                    >
                      -
                    </button>
                    <span className="ability-value">{character.abilityScores[ability]}</span>
                    <button
                      onClick={() => handleAbilityChange(ability, 1)}
                      disabled={character.abilityScores[ability] >= 15}
                    >
                      +
                    </button>
                  </div>
                  <span className="ability-modifier">
                    ({Math.floor((character.abilityScores[ability] - 10) / 2) >= 0 ? '+' : ''}
                    {Math.floor((character.abilityScores[ability] - 10) / 2)})
                  </span>
                </div>
              ))}
            </div>
          </div>
        );

      case 7: // Languages
        return (
          <div className="step-content">
            <h2>Choose Languages</h2>
            <p className="step-description">Select the languages your character speaks (Common is always included)</p>
            <div className="language-grid">
              {creationData?.languages.map(lang => (
                <div
                  key={lang.id}
                  className={`language-chip ${character.languages.includes(lang.id) ? 'selected' : ''} ${lang.id === 'common' ? 'required' : ''}`}
                  onClick={() => lang.id !== 'common' && toggleLanguage(lang.id)}
                >
                  {lang.name}
                  {lang.type === 'exotic' && <span className="lang-type">Exotic</span>}
                  {lang.type === 'secret' && <span className="lang-type">Secret</span>}
                </div>
              ))}
            </div>
          </div>
        );

      case 8: // Starting Gear
        return (
          <div className="step-content">
            <h2>Choose Starting Equipment</h2>
            <p className="step-description">Select your starting weapon and clothing</p>

            <div className="form-group">
              <CustomSelect
                label="Starting Weapon"
                placeholder="-- Select a weapon --"
                value={character.selectedWeapon}
                onChange={(val) => updateCharacter('selectedWeapon', val)}
                options={creationData?.weapons?.map(w => ({
                  id: w.name,
                  name: w.name,
                  damage: w.damage,
                  damageType: w.damageType,
                  type: w.type
                })) || []}
                renderOption={(weapon) => (
                  <div className="weapon-option">
                    <span className="weapon-name">{weapon.name}</span>
                    <span className="weapon-stats">{weapon.damage} {weapon.damageType}</span>
                  </div>
                )}
                renderSelected={(weapon) => (
                  <span>{weapon.name} ({weapon.damage})</span>
                )}
              />
            </div>

            <div className="form-group">
              <CustomSelect
                label="Starting Clothing"
                placeholder="-- Select clothing --"
                value={character.selectedClothing}
                onChange={(val) => updateCharacter('selectedClothing', val)}
                options={creationData?.clothing?.map(c => ({
                  id: c.name,
                  name: c.name,
                  description: c.description
                })) || []}
                renderOption={(item) => (
                  <div className="clothing-option">
                    <span className="clothing-name">{item.name}</span>
                    {item.description && <span className="clothing-desc">{item.description}</span>}
                  </div>
                )}
              />
            </div>

            <div className="starting-items-note card">
              <p><strong>You will also start with:</strong></p>
              <ul>
                <li>Class-specific starting equipment</li>
                <li>A full waterskin</li>
                <li>A loaf of stale bread</li>
                <li>Starting gold from your background</li>
              </ul>
            </div>
          </div>
        );

      case 9: // Spells
        if (!isSpellcaster()) {
          return (
            <div className="step-content">
              <h2>Spells</h2>
              <p className="step-description">Your class ({capitalize(character.class)}) is not a spellcaster.</p>
              <p className="text-muted">Click "Next" to continue to the review.</p>
            </div>
          );
        }

        const classData = getSelectedClassData();
        const maxCantrips = classData?.cantripsKnown?.[1] || 0;
        const maxSpells = classData?.spellsKnown?.[1] || 2;

        return (
          <div className="step-content">
            <h2>Choose Your Spells</h2>

            {maxCantrips > 0 && (
              <>
                <h3>Cantrips ({character.selectedCantrips.length}/{maxCantrips})</h3>
                <p className="step-description">Cantrips can be cast at will</p>
                <div className="spell-grid">
                  {creationData?.cantrips
                    .filter(c => c.classes.includes(character.class))
                    .map(cantrip => (
                      <div
                        key={cantrip.name}
                        className={`spell-card ${character.selectedCantrips.includes(cantrip.name) ? 'selected' : ''}`}
                        onClick={() => toggleCantrip(cantrip.name)}
                      >
                        <h4>{cantrip.name}</h4>
                        <p className="spell-school">{cantrip.school}</p>
                        <p className="spell-desc">{cantrip.description}</p>
                      </div>
                    ))}
                </div>
              </>
            )}

            <h3 className="mt-3">Level 1 Spells ({character.selectedSpells.length}/{maxSpells})</h3>
            <p className="step-description">Choose your known spells</p>
            <div className="spell-grid">
              {creationData?.spells
                .filter(s => s.classes.includes(character.class))
                .map(spell => (
                  <div
                    key={spell.name}
                    className={`spell-card ${character.selectedSpells.includes(spell.name) ? 'selected' : ''}`}
                    onClick={() => toggleSpell(spell.name)}
                  >
                    <h4>{spell.name}</h4>
                    <p className="spell-school">{spell.school}</p>
                    <p className="spell-desc">{spell.description}</p>
                  </div>
                ))}
            </div>
          </div>
        );

      case 10: // Review
        return (
          <div className="step-content">
            <h2>Review Your Character</h2>
            <div className="review-card card card-gold">
              <div className="review-header">
                {character.appearance.portraitUrl && (
                  <img
                    src={character.appearance.portraitUrl}
                    alt={`Portrait of ${character.name}`}
                    className="review-portrait"
                  />
                )}
                <div className="review-header-text">
                  <h3 className="review-name">{character.name}</h3>
                  <p className="review-title">
                    Level 1 {capitalize(character.species.primary)}
                    {character.species.secondary && ` / ${capitalize(character.species.secondary)}`} {capitalize(character.class)}
                  </p>
                </div>
              </div>

              <div className="review-section">
                <h4>Background</h4>
                <p>
                  {capitalize(character.background.name)}
                  {character.background.guild && ` - ${character.background.guild.name}`}
                </p>
                {character.background.guild && (
                  <p className="text-muted">Tool Proficiency: {character.background.guild.tools}</p>
                )}
                {character.background.story && <p className="text-muted">{character.background.story}</p>}
              </div>

              <div className="review-section">
                <h4>Appearance</h4>
                <p>{capitalize(character.gender.replace('-', ' '))}, {character.appearance.height}cm, {character.appearance.weight}kg, {character.appearance.age} years old</p>
                {character.appearance.description && <p className="text-muted">{character.appearance.description}</p>}
              </div>

              <div className="review-section">
                <h4>Ability Scores</h4>
                <div className="review-abilities">
                  {ABILITY_NAMES.map(ability => (
                    <span key={ability}>
                      {ability.slice(0, 3).toUpperCase()}: {character.abilityScores[ability]}
                    </span>
                  ))}
                </div>
              </div>

              <div className="review-section">
                <h4>Languages</h4>
                <p>{character.languages.map(capitalize).join(', ')}</p>
              </div>

              {character.selectedWeapon && (
                <div className="review-section">
                  <h4>Starting Weapon</h4>
                  <p>{character.selectedWeapon}</p>
                </div>
              )}

              {isSpellcaster() && character.selectedCantrips.length > 0 && (
                <div className="review-section">
                  <h4>Cantrips</h4>
                  <p>{character.selectedCantrips.join(', ')}</p>
                </div>
              )}

              {isSpellcaster() && character.selectedSpells.length > 0 && (
                <div className="review-section">
                  <h4>Spells</h4>
                  <p>{character.selectedSpells.join(', ')}</p>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="character-create">
      <div className="create-header">
        <button onClick={() => navigate('/play')} className="btn btn-secondary">
          Back to Dashboard
        </button>
        <h1>Create Character</h1>
        <div className="step-indicator">
          Step {step + 1} of {STEPS.length}
        </div>
      </div>

      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}></div>
      </div>

      <div className="steps-nav">
        {STEPS.map((stepName, idx) => (
          <div
            key={stepName}
            className={`step-dot ${idx === step ? 'active' : ''} ${idx < step ? 'completed' : ''}`}
            title={stepName}
          />
        ))}
      </div>

      {error && <div className="create-error">{error}</div>}

      <div className="step-container">
        {renderStep()}
      </div>

      <div className="create-footer">
        <button
          onClick={() => setStep(s => s - 1)}
          className="btn btn-secondary"
          disabled={step === 0}
        >
          Previous
        </button>

        {step < STEPS.length - 1 ? (
          <button
            onClick={() => setStep(s => s + 1)}
            className="btn btn-primary"
            disabled={!canProceed()}
          >
            Next
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Creating...' : 'Create Character'}
          </button>
        )}
      </div>
    </div>
  );
}
